import React from 'react';
import { Grid, TreeNavigation, ButtonBar, Alert, Loader } from 'react-unity';
import { Button, ButtonSizeType } from '../common/components/Button';
import {
	OnPremAbstractPORForm,
	AbstractPORFormState,
	AbstractPORFormProps,
	IPORStateForm,
} from './OnPremAbstractPORForm';
import WorkflowDetailWrapper from '../common/wrappers/OnPremWorkflowDetailWrapper';
import PortOpeningRequestOnPrem from '../../models/entities/PortOpeningRequestOnPrem';
import { OnPremPORTypeInput } from './components/OnPremPORTypeInput';
import { OnPremRulesTable } from './components/OnPremRulesTable';
import TextInputField from '../../components/common/form-controls/TextInputField';
import UserLookup from '../common/form-controls/Lookups/UserLookup';
import TextAreaField from '../common/form-controls/TextareaField';
import AlertModal from '../common/modals/AlertModal';
import ConfirmModal from '../common/modals/ConfirmModal';
import { WorkflowState } from '../../models/enums/WorkflowState';
import OnPremPortOpeningRequestsCreate from './OnPremPortOpeningRequestsCreate';
import './OnPremPortOpeningRequestsDetail.css';
import { useParams, useNavigate } from 'react-router-dom';
import ApprovePortOpeningRequestOnPrem from '../../models/viewModels/OnPremPortOpeningRequests/ApprovePortOpeningRequestOnPrem';
import CompletePortOpeningRequestOnPrem from '../../models/viewModels/OnPremPortOpeningRequests/CompletePortOpeningRequestOnPrem';
import { ApproverInput } from '../PortOpeningRequests/components';
import CartaInput from '../common/form-controls/Lookups/CartaInput';
import { OnPremPORScopeInput } from './components/OnPremPORScopeInput';
import { OnPremPortOpeningRequestsTypes } from '../../models/enums/POR/OnPremPortOpeningRequestsTypes';
import SelectField from '../common/form-controls/SelectField';
import RejectionModel from '../../models/interfaces/RejectionModel';
import UpdatePortOpeningRequestOnPrem from '../../models/viewModels/OnPremPortOpeningRequests/UpdatePortOpeningOnPremRequest';
import ProgressBarBig from './components/ProgressBarBig';
import UpdatePortOpeningRequestRuleOnPrem from '../../models/viewModels/OnPremPortOpeningRequests/UpdatePortOpeningOnPremRule';
import {ToggleField} from '../common/';
import { Validation } from '../../utils/Validation';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import OnPremRiskPorts from '../../models/entities/OnPremRiskPorts';
import { Icon } from '../common/components/Icon';

interface PortOpeningRequestsDetailWithParamsProps extends AbstractPORFormProps {}

interface StateForm extends IPORStateForm {
	selectedType: string;
}

interface PortOpeningRequestsDetailWithParamsState extends AbstractPORFormState<StateForm> {
	escalationType?: string | null;
	loading: boolean;
	portOpeningRequestOnPrem?: PortOpeningRequestOnPrem;
	editMode: boolean;
	matchPatch: string;
	decisionModalVisible: {
		approve?: boolean;
		reject?: boolean;
		review?: boolean;
		cancel?: boolean;
		escalate?: boolean;
		complete?: boolean;
		draft?: boolean;
		resubmit?: boolean;
		createCHG?: boolean;
	};
	modalComment: string;
	selectedFromExisting: string[];
	risksPorts: OnPremRiskPorts;
	riskPortAlerts: { hasCritical: boolean; hasHigh: boolean };
}

class PortOpeningRequestsDetailWithParams extends OnPremAbstractPORForm<
	PortOpeningRequestsDetailWithParamsProps,
	PortOpeningRequestsDetailWithParamsState
> {
	constructor(props) {
		super(props);

		this.state = {
			loading: true,
			stateForm: {
				selectedType: '',
				contact: null,
				approverDOAG: null,
				trackerID: null,
				firewall: '',
				businessJustification: '',
				changeTicketId: null,
				ruleList: [],
				cartaId: [],
			},
			editMode: false,
			submissionWarned: false,
			operationInProgress: false,
			endModal: {
				visible: false,
			},
			submissionAlert: {
				visible: false,
			},
			decisionModalVisible: {},
			modalComment: '',
			selectedFromExisting: [],
			matchPatch: null,
			escalationType: null,
			implementedRules: [],
			failedImplementedRules: false,
			loadingImplementedRules: false,
			risksPorts: null,
			riskPortAlerts: { hasCritical: false, hasHigh: false },
		};

		this.initFormModel();
	}

	initFormModel() {
		super.initFormModel();

		this.formModel.addField('type', {
			getValue: () => this.state.stateForm.selectedType,
			validation: {
				required: true,
			},
		});
		this.formModel.fields.trackerID.validation = new Validation({
			required: () =>
				this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternalCertConnection.name,
			rules: [
				{
					assert: () => /^[0-9]*$/.test(this.state.stateForm.trackerID?.toString() ?? ''),
					message: 'Only numbers are valid values',
				},
				{
					assert: () => this.state.stateForm.trackerID >= 10000,
					message: 'Tracker ID must be greater than 10000',
				},
			],
		});

		this.formModel.fields.cartaId.validation = new Validation({
			required: () => this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternetDMZ.name,
		});

		this.formModel.fields.appEndorserId.validation = new Validation({
			required: () => this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternetDMZ.name,
		});

		this.formModel.fields.approverId.validation = new Validation({
			required: this.requiresDOAGApproval(this.state.portOpeningRequestOnPrem?.type?.name) ? true : false
		});

		this.formModel.fields.ownerDelegate.validation = new Validation({
			required: () =>
				this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternalCertConnection.name,
		});
		this.formModel.fields.firewall.validation = new Validation({
			required: () =>
				this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternalCertConnection.name,
		});

		this.formModel.fields.firewallEnvironment.validation = new Validation({
			required: () =>
				this.state.stateForm.selectedType === OnPremPortOpeningRequestsTypes.InternalCertConnection.name,
		});

		this.formModel.addField('onPremPortOpeningRequestId', {
			getValue: () => this.state.portOpeningRequestOnPrem?.id,
		});

		this.formModel.fields.portOpeningRules.getValue = () =>
			this.state.stateForm.ruleList.map((rule) => new UpdatePortOpeningRequestRuleOnPrem(rule));
	}

	async componentDidMount() {
		const id = parseInt(this.props.params.porId);
		await this.loadRequest(id);
	}

	loadRequest = async (id: number) => {
		this.setState(
			{
				loading: true,
			},
			async () => {
				try {
					const request = await this.portOpeningRequestServiceOnPrem.getById(id);
					const portOpeningRequestOnPrem = new PortOpeningRequestOnPrem(request);
					this.setState(
						{
							portOpeningRequestOnPrem,
						},
						this.resetStateForm
					);

					this.setState({
						selectedFromExisting: this.state.stateForm.ruleList.map((rule) => rule.name),
					});

					if (
						portOpeningRequestOnPrem.type.name == OnPremPortOpeningRequestsTypes.InternalCertConnection.name
					) {
						portOpeningRequestOnPrem.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState ==
							WorkflowState.Review && portOpeningRequestOnPrem.isEditable()
							? await this.handleApproverDelegateOptions(portOpeningRequestOnPrem.trackerID, true)
							: this.loadOptions(portOpeningRequestOnPrem);
					} else {
						portOpeningRequestOnPrem.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState ==
							WorkflowState.Review && portOpeningRequestOnPrem.isEditable()
							? await this.handleAppEndorserOptions(portOpeningRequestOnPrem.cartaId[0]?.cartaAppId)
							: this.loadOptions(portOpeningRequestOnPrem);
					}

					const risksPorts = await this.getRisksPorts();

					this.setState({
						loading: false,
						risksPorts,
					});

					if (portOpeningRequestOnPrem.type.name == OnPremPortOpeningRequestsTypes.InternetDMZ.name)
						this.getCartaIdName(portOpeningRequestOnPrem.cartaId[0].cartaAppId);
				} catch (error) {
					const HTTP_FORBIDDEN_ERROR_CODE = 403;

					if (error.status === HTTP_FORBIDDEN_ERROR_CODE) {
						this.handleEndModal(
							'You are not allowed to see this request.',
							'error',
							5000,
							'/onPremPortOpeningRequests'
						);
					} else {
						this.handleEndModal(
							'The request could not be retrieved.',
							'error',
							10000,
							'/onPremPortOpeningRequests'
						);
					}
				}
			}
		);
	};

	resetStateForm = () => {
		this.setState((prevState) => ({
			stateForm: this.stateFormFromRequest(prevState.portOpeningRequestOnPrem),
		}));
	};

	loadOptions = (por: PortOpeningRequestOnPrem) => {
		const options = [];
		const firewallOptions = [];
		switch (por.type) {
			case OnPremPortOpeningRequestsTypes.InternalCertConnection:
				options.push({ value: por.approvingUser.id, text: por.approvingUser.displayName });
				firewallOptions.push({ value: por.firewall, text: por.firewall });
				this.setState({ approverDelegateOptions: options, firewallOptions: firewallOptions });
				break;
			case OnPremPortOpeningRequestsTypes.InternetDMZ:
				options.push({
					value: por.appEndorser.customFields.contact_email,
					text: por.appEndorser.customFields.first_name + ' ' + por.appEndorser.customFields.last_name,
				});
				this.setState({ appEndorserOptions: options });
				break;
		}
	};

	stateFormFromRequest(por: PortOpeningRequestOnPrem): StateForm {
		const currentState =
			this.state.portOpeningRequestOnPrem.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState.value;
		if (currentState === WorkflowState.Review.value || currentState === WorkflowState.Draft.value) {
			let obj = {
				...super.stateFormFromRequest(por),
				selectedType: por.type?.name || new OnPremPortOpeningRequestsTypes().fromName('InternetDMZ').name, //'',
			};
			return {
				...obj,
				ruleList: obj.ruleList.map((rule) => {
					return { ...rule, azureName: undefined };
				}),
			};
		}
		return {
			...super.stateFormFromRequest(por),
			selectedType: por.type?.name || new OnPremPortOpeningRequestsTypes().fromName('InternetDMZ').name, //'',
		};
	}

	setSelectedFromExisting = (rulesName: string[]) => {
		this.setState({
			selectedFromExisting: rulesName,
		});
	};

	finishReview = async () => {
		this.openDecisionModal('resubmit');
	};

	actions = () =>
		!this.state.operationInProgress && (
			<TreeNavigation className="em-u-clickable">
				{this.state.portOpeningRequestOnPrem?.isEditable() && (
					<>
						<TreeNavigation.Item
							label="Edit"
							className="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={this.enableEdit}
						/>
						<br />
						<br />
					</>
				)}
				{(this.state.portOpeningRequestOnPrem?.canHaveDOAGApproval() ||
					this.state.portOpeningRequestOnPrem?.canHaveAppOwnerApproval() ||
					this.state.portOpeningRequestOnPrem?.canHaveConnOwnerApproval() ||
					this.state.portOpeningRequestOnPrem?.canHaveReviewerApproval() ||
					this.state.portOpeningRequestOnPrem?.canHaveImplementerApproval() ||
					this.state.portOpeningRequestOnPrem?.canHaveProxyApproval() ||
					this.state.portOpeningRequestOnPrem?.canHaveOTCRMApproval() ||
					this.state.portOpeningRequestOnPrem?.canHaveCyberSecApproval() ||
					this.state.portOpeningRequestOnPrem?.canHaveSolutionArchitectApproval() ||
					this.state.portOpeningRequestOnPrem?.canEscalateApproval() ||
					this.state.portOpeningRequestOnPrem?.canHaveProxyArchApproval() ||
					(this.state.portOpeningRequestOnPrem?.canEscalateToCyberSec() &&
						!this.state.portOpeningRequestOnPrem?.canContinueRequest())) && (
					<>
						<TreeNavigation.Item
							label={
								this.state.portOpeningRequestOnPrem.workflowInstance.currentWorkflowInstanceState
									.onPremWorkflowState.value == WorkflowState.AwaitingImplementation.value
									? 'Success'
									: 'Approve'
							}
							className="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								if (
									this.state.portOpeningRequestOnPrem.workflowInstance.currentWorkflowInstanceState
										.onPremWorkflowState.value == WorkflowState.AwaitingImplementation.value
								) {
									this.openDecisionModal('complete', 'success');
								} else {
									this.openDecisionModal('approve');
								}
							}}
						/>
						<br />
						<br />
						<TreeNavigation.Item
							label={
								this.state.portOpeningRequestOnPrem.workflowInstance.currentWorkflowInstanceState
									.onPremWorkflowState.value == WorkflowState.AwaitingImplementation.value
									? 'Failure'
									: 'Reject'
							}
							className="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								if (
									this.state.portOpeningRequestOnPrem.workflowInstance.currentWorkflowInstanceState
										.onPremWorkflowState.value == WorkflowState.AwaitingImplementation.value
								) {
									this.openDecisionModal('complete');
								} else {
									this.openDecisionModal('reject');
								}
							}}
						/>
						<br />
						<br />
						{this.state.portOpeningRequestOnPrem?.canEscalateApproval() && (
							<>
								{this.state.portOpeningRequestOnPrem.direction !==
									PortOpeningRequestsDirection.iBossProxyToInternet && (
									<>
										<TreeNavigation.Item
											label={'Escalate to Solution Architect'}
											className="em-c-btn em-c-btn--secondary em-c-btn--small"
											onClick={() => {
												this.openDecisionModal('escalate', 'SolutionArchitect');
											}}
										/>
										<br />
										<br />
										{this.state.portOpeningRequestOnPrem.type ===
											OnPremPortOpeningRequestsTypes.InternalCertConnection && (
											<>
												<TreeNavigation.Item
													label={'Escalate to OTCRM'}
													className="em-c-btn em-c-btn--secondary em-c-btn--small"
													onClick={() => {
														this.openDecisionModal('escalate', 'OTCRM');
													}}
												/>
												<br />
												<br />
											</>
										)}
									</>
								)}
								{this.state.portOpeningRequestOnPrem.type ===
									OnPremPortOpeningRequestsTypes.InternetDMZ &&
									this.state.portOpeningRequestOnPrem.direction ===
										PortOpeningRequestsDirection.iBossProxyToInternet && (
										<>
											<TreeNavigation.Item
												label={'Escalate to Proxy Architect'}
												className="em-c-btn em-c-btn--secondary em-c-btn--small"
												onClick={() => {
													this.openDecisionModal('escalate', 'ProxyArchitect');
												}}
											/>
											<br />
											<br />
										</>
									)}
							</>
						)}
						{this.state.portOpeningRequestOnPrem?.canEscalateToCyberSec() && (
							<>
								<TreeNavigation.Item
									label={'Escalate to CyberSec'}
									className="em-c-btn em-c-btn--secondary em-c-btn--small"
									onClick={() => {
										this.openDecisionModal('escalate', 'CyberSec');
									}}
								/>
								<br />
								<br />
							</>
						)}
					</>
				)}
				{this.state.portOpeningRequestOnPrem?.canSendToReview() && (
					<>
						<TreeNavigation.Item
							label="Send to Review"
							className="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('review');
							}}
						/>
						<br />
						<br />
					</>
				)}
				{this.state.portOpeningRequestOnPrem?.canSendToDraft() && (
					<>
						<TreeNavigation.Item
							label="Send to Draft"
							className="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('draft');
							}}
						/>
						<br />
						<br />
					</>
				)}
				{this.state.portOpeningRequestOnPrem?.isCancellable() && (
					<>
						<TreeNavigation.Item
							label="Cancel Request"
							className="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('cancel');
							}}
						/>
						<br />
						<br />
					</>
				)}
				{this.state.portOpeningRequestOnPrem?.canCreateCHG() && (
					<>
						<TreeNavigation.Item
							label="Create CHG"
							className="em-c-btn em-c-btn--secondary em-c-btn--small"
							onClick={() => {
								this.openDecisionModal('createCHG');
							}}
						/>
						<br />
						<br />
					</>
				)}
			</TreeNavigation>
		);

	editActions = (size: ButtonSizeType) => (
		<ButtonBar className="edit-button-bar">
			{!this.state.operationInProgress && (
				<>
					<ButtonBar.Item>
						<Button size={size} variant="secondary" onClick={this.cancelEdit}>
							<div className="action-button__icon">
								<Icon size={size} icon="x-filled" />
							</div>
							<span>Cancel</span>
						</Button>
					</ButtonBar.Item>
					<ButtonBar.Item separator />
				</>
			)}
			<ButtonBar.Item>
				<Button
					size={size}
					variant="primary"
					disabled={!this.formModel.isValid() || this.state.operationInProgress}
					onClick={() => this.finishReview()}
				>
					{this.state.operationInProgress ? (
						<>
							<Loader className="icon-loader em-c-icon--small" />
							<span>Loading...</span>
						</>
					) : (
						<>
							<div className="action-button__icon">
								<Icon size={size} icon="save-disk" />
							</div>
							<span>Finish Review</span>
						</>
					)}
				</Button>
			</ButtonBar.Item>
		</ButtonBar>
	);

	enableEdit = async () => {
		this.setState({
			editMode: true,
		});
	};

	cancelEdit = () => {
		this.setState({ editMode: false });
		this.resetStateForm();
	};

	openDecisionModal = (
		type: 'approve' | 'reject' | 'review' | 'cancel' | 'complete' | 'draft' | 'escalate' | 'resubmit' | 'createCHG',
		escalationType?: string
	) => {
		this.setState({
			decisionModalVisible: {
				[type]: true,
			},
			escalationType: escalationType || null,
		});
	};

	closeDecisionModal = () => {
		this.setState({
			decisionModalVisible: {},
			modalComment: '',
		});
	};

	setEmbedAlertSuccess = (message) => {
		this.setState(
			{
				submissionAlert: {
					variant: 'success',
					text: message,
					visible: true,
				},
			},
			() => {
				setTimeout(this.handleSubmissionAlertClose, 5000);
			}
		);
	};

	decisionModalConfirmationField = () => ({
		label: this.state.decisionModalVisible.resubmit
			? 'Please, specify what changes have been made.'
			: 'Please, provide the reason.',
		value: this.state.modalComment,
		validation: {
			required: true,
		},
		onChange: (ev) => {
			this.setState({
				modalComment: ev.target.value,
			});
		},
	});

	handleApprove = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				let model: ApprovePortOpeningRequestOnPrem = {
					IsApproved: true,
					Reason: this.state.modalComment,
					ChangeTicketId: this.state.stateForm.changeTicketId,
				};
				await this.portOpeningRequestServiceOnPrem.doApproval(model, this.state.portOpeningRequestOnPrem?.id);
			},
			inProgressMessage: 'Approving...',
			successMessage: 'Request approved sucessfully.',
		});
	};

	handleReject = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				let rejectionModel: RejectionModel = { rejectionReason: this.state.modalComment };
				await this.portOpeningRequestServiceOnPrem.doRejection(
					rejectionModel,
					this.state.portOpeningRequestOnPrem?.id
				);
			},
			inProgressMessage: 'Rejecting...',
			successMessage: 'Request rejected sucessfully.',
		});
	};

	handleEscalate = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				const model = {
					reviewReason: this.state.modalComment,
					OTCRMApproval: this.state.escalationType === 'OTCRM',
					proxyArchitectApproval: this.state.escalationType === 'ProxyArchitect',
				};
				await this.portOpeningRequestServiceOnPrem.doEscalation(model, this.state.portOpeningRequestOnPrem?.id);
			},
			inProgressMessage: 'Escalating...',
			successMessage: 'Request escalated successfully.',
		});
	};

	handleReview = async () => {
		await this.handleOperationConfirmed({
			operation: async () => {
				const model = { reviewReason: this.state.modalComment };
				await this.portOpeningRequestServiceOnPrem.doReviewal(model, this.state.portOpeningRequestOnPrem?.id);
			},
			inProgressMessage: 'Sending to review...',
			successMessage: 'Request sent to review successfully.',
		});
	};

	handleDraft = async () => {
		await this.handleOperationConfirmed({
			operation: async () => {
				const model = { reviewReason: this.state.modalComment };
				await this.portOpeningRequestServiceOnPrem.sendToDraft(model, this.state.portOpeningRequestOnPrem?.id);
			},
			inProgressMessage: 'Sending to draft...',
			successMessage: 'Request sent to review successfully.',
		});
	};

	handleComplete = async () => {
		await this.handleOperationConfirmed({
			operation: async () => {
				let model: CompletePortOpeningRequestOnPrem = {
					IsApproved: true,
					Successful: this.state.escalationType === 'success',
					Reason: this.state.modalComment,
					ChangeTicketId: this.state.stateForm.changeTicketId,
				};
				await this.portOpeningRequestServiceOnPrem.doComplete(model, this.state.portOpeningRequestOnPrem?.id);
			},
			inProgressMessage: 'Completing...',
			successMessage: 'Request completed successfully.',
		});
	};

	handleCancellation = async () => {
		this.handleOperationConfirmed({
			operation: async () => {
				await this.portOpeningRequestServiceOnPrem.cancel(this.state.portOpeningRequestOnPrem?.id, {
					cancellationReason: this.state.modalComment,
				});
			},
			inProgressMessage: 'Cancelling...',
			successMessage: 'Request cancelled successfully.',
		});
	};

	handleUpdate = async () => {
		await this.handleOperationConfirmed({
			operation: async () => {
				let model = this.formModel.create(UpdatePortOpeningRequestOnPrem);
				model.updateReason = this.state.modalComment;
				console.log('Hola este es el modelo que cambia de POR: ');
				console.log(model);
				await this.portOpeningRequestServiceOnPrem.update(this.state.portOpeningRequestOnPrem?.id, model);
				this.setState({
					editMode: false,
				});
			},
			inProgressMessage: 'Updating request...',
			successMessage: 'Your request was updated successfully.',
		});
	};

	handleOperationConfirmed = async ({ operation, inProgressMessage, successMessage }) => {
		this.setSubmissionAlertInProgress(inProgressMessage);
		this.closeDecisionModal();
		try {
			await operation();
			await this.loadRequest(this.state.portOpeningRequestOnPrem?.id);
			this.setEmbedAlertSuccess(successMessage);
		} catch (err) {
			const HTTP_INTERNAL_SERVER_ERROR_CODE = 500;
			if (err.status === HTTP_INTERNAL_SERVER_ERROR_CODE && err?.body?.message) {
				await this.loadRequest(this.state.portOpeningRequestOnPrem?.id);
			}
			this.setSubmissionAlertError(
				err?.response?.data?.message ??
					'An error occured when processing your request. Please, try again later.'
			);
		} finally {
			this.setState({
				operationInProgress: false,
				modalComment: '',
			});
		}
	};

	handleCreateCHG = async () => {
		await this.handleOperationConfirmed({
			operation: async () => {
				await this.portOpeningRequestServiceOnPrem.createCHG(this.state.portOpeningRequestOnPrem?.id);
			},
			inProgressMessage: 'Creating Change Ticket...',
			successMessage: 'Change Ticket created sucessfully.',
		});
		await this.loadRequest(this.state.portOpeningRequestOnPrem?.id);
	};

	render() {
		return !this.state.loading &&
			this.state.portOpeningRequestOnPrem != null &&
			this.state.portOpeningRequestOnPrem?.isEditable() &&
			this.state.portOpeningRequestOnPrem?.workflowInstance.currentWorkflowInstanceState.onPremWorkflowState
				.value === WorkflowState.Draft.value ? (
			<OnPremPortOpeningRequestsCreate draftRequest={this.state.portOpeningRequestOnPrem} />
		) : (
			<>
				<WorkflowDetailWrapper
					title={`Port Opening Request #${this.state.portOpeningRequestOnPrem?.id}`}
					workflowInstance={this.state.portOpeningRequestOnPrem?.workflowInstance}
					loading={this.state.loading}
					linkTree={this.getLinkTree()}
					actions={this.state.editMode ? this.editActions('small') : this.actions()}
					fromPOR
					currentApprovers={this.state.portOpeningRequestOnPrem?.currentApprovers()}
				>
					{this.state.submissionAlert.visible && (
						<Alert variant={this.state.submissionAlert.variant} onClose={this.handleSubmissionAlertClose}>
							{this.state.submissionAlert.text}
						</Alert>
					)}
					<Grid variant="halves">
						<Grid.Item>
							<OnPremPORTypeInput
								type={{
									value: this.state.portOpeningRequestOnPrem?.type?.name,
									disabled: true,
								}}
							/>
						</Grid.Item>
					</Grid>
					<Grid variant="2-up">
						<Grid.Item>
							<OnPremPORScopeInput
								direction={{
									value: this.state.portOpeningRequestOnPrem?.direction?.name,
									disabled: true,
								}}
								type={this.state.stateForm.selectedType}
							/>
						</Grid.Item>
						<Grid.Item>
							<TextInputField
								label="Change Request Ticket"
								{...this.stateFormHandler().changeTicketId}
								disabled={true}
							/>
						</Grid.Item>
					</Grid>
					{this.requiresApproverVerification(this.state.portOpeningRequestOnPrem?.type?.name) && (
						<Grid variant="2-up">
							<Grid.Item>
								<TextInputField
									label="Tracker Id"
									{...this.stateFormHandler().trackerID}
									disabled={this.state.operationInProgress || !this.state.editMode}
									//scope={this.state.stateForm?.selectedType}
									readOnly={this.state.loadingTracker}
									note={
										this.state.loadingTracker
											? "Validating..."
											: this.state.failedTracker
											? this.state.errorTracker
											: ""
									}
								/>
							</Grid.Item>
							<Grid.Item>
								<SelectField
									label="Firewall"
									aria-placeholder={this.stateFormHandler().firewall.value}
									className="full-width"
									{...this.stateFormHandler().firewall}
									disabled={
										this.state.operationInProgress ||
										this.stateFormHandler().firewall.options.length == 0 ||
										this.state.loadingTracker ||
										!this.state.editMode
									}
									isLoading={this.stateFormHandler().firewall.options.length == 0}
									note={
										this.stateFormHandler().firewall.options.length > 0 ? (
											<></>
										) : (
											<span id="firewallError">
												No NCP is linked to the Tracker ID in NCDB, please contact
												GSC-EMIT-NETSEC-RCS /SM to help remediate
											</span>
										)
									}
								/>
							</Grid.Item>
						</Grid>
					)}
					<Grid variant="halves">
						<Grid.Item>
							{this.requiresDOAGApproval(this.state.portOpeningRequestOnPrem?.type?.name) && (
								<ApproverInput
									{...this.stateFormHandler().approverDOAG}
									disabled={this.state.operationInProgress || !this.state.editMode}
								/>
							)}
							{this.requiresApproverVerification(this.state.portOpeningRequestOnPrem?.type?.name) && (
								<SelectField
									label="Approver"
									aria-placeholder={this.stateFormHandler().ownerDelegate.value}
									className="full-width"
									{...this.stateFormHandler().ownerDelegate}
									disabled={
										this.state.operationInProgress ||
										this.stateFormHandler().ownerDelegate.options.length == 0 ||
										this.state.loadingTracker ||
										!this.state.editMode
									}
									isLoading={this.stateFormHandler().ownerDelegate.options.length == 0}
								/>
							)}
						</Grid.Item>
						<Grid.Item>
							<UserLookup
								label="Technical contact"
								{...this.stateFormHandler().contact}
								disabled={this.state.operationInProgress || !this.state.editMode}
							/>
						</Grid.Item>
					</Grid>
					{this.requiresAppEndorserApproval(this.state.portOpeningRequestOnPrem?.type?.name) && (
						<Grid variant="2-up">
							<Grid.Item>
								<CartaInput
									{...this.stateFormHandler().cartaId}
									oneOptionOnly={this.state.stateForm.cartaId.length > 0}
									disabled={this.state.operationInProgress || !this.state.editMode}
									hideNotes={this.state.operationInProgress || !this.state.editMode}
									hideTooltip={false}
								/>
							</Grid.Item>
							<Grid.Item>
								<SelectField
									label="Approver (Business Owner, IT Owner, IT Architect)"
									className="full-width"
									{...this.stateFormHandler().appEndorser}
									disabled={
										this.state.operationInProgress ||
										this.stateFormHandler().appEndorser.options.length == 0 ||
										!this.state.editMode ||
										this.stateFormHandler().cartaId.value.length == 0
									}
									isLoading={this.stateFormHandler().appEndorser.options.length === 0}
									note={
										(this.stateFormHandler().appEndorser.options.length == 0 ||
											this.stateFormHandler().cartaId.value.length == 0) &&
										'First select the DBM'
									}
								/>
							</Grid.Item>
						</Grid>
					)}
					{this.requiresApproverVerification(this.state.stateForm?.selectedType) && (
						<Grid variant="2-up">
							<Grid.Item>
								<ToggleField
									{...this.stateFormHandler().siteCoordination}
									id='siteCoordination'
									label="Site Coordination Required"
									options={[
										{ value: 'true', text: 'Yes' },
										{ value: 'false', text: 'No' },
									]}
									disabled={this.state.operationInProgress || !this.state.editMode}
								/>
							</Grid.Item>
							<Grid.Item>
								<SelectField
									label="Firewall Environment"
									className="full-width"
									{...this.stateFormHandler().firewallEnvironment}
									disabled={this.state.operationInProgress || !this.state.editMode}
								/>
							</Grid.Item>
						</Grid>
					)}
					<Grid>
						<Grid.Item>
							<ProgressBarBig
								workflowState={
									this.state.portOpeningRequestOnPrem?.workflowInstance.currentWorkflowInstanceState
										.onPremWorkflowState
								}
								por={this.state.portOpeningRequestOnPrem}
								type={this.state.portOpeningRequestOnPrem?.type?.name || ''}
								scope={this.state.portOpeningRequestOnPrem?.direction?.name || ''}
							/>
						</Grid.Item>
					</Grid>
					<TextAreaField
						label="Business Justification"
						{...this.stateFormHandler().businessJustification}
						disabled={this.state.operationInProgress || !this.state.editMode}
					/>
						{this.assertIpsContainLargeSubnets() && (
							<Alert variant="warning">
								Large subnets have been detected. Please provide a detailed Business justification for their
								use.
							</Alert>
						)}
						{this.state.riskPortAlerts.hasHigh && (
						<Alert variant="warning">The POR contains High Risk Ports</Alert>
					)}
					{this.state.riskPortAlerts.hasCritical && (
						<Alert variant="error">The POR contains Critical Risk Ports</Alert>
					)}
					<OnPremRulesTable
						{...this.stateFormHandler().ruleset}
						porType={this.state.portOpeningRequestOnPrem?.type}
						readonly={this.state.operationInProgress || !this.state.editMode}
						currentWorkflowState={
							this.state.portOpeningRequestOnPrem?.workflowInstance.currentWorkflowInstanceState
								.onPremWorkflowState.value
						}
						fromDetails
						selectedFromExisting={this.state.selectedFromExisting}
						setSelectedFromExisting={this.setSelectedFromExisting}
						implementedRules={this.state.implementedRules}
						risksPorts={this.state.risksPorts}
						hasRiskPorts={this.handleStateFormChange.bind(this, 'hasRiskPorts')}
						onRiskTypeChange={(types) => this.setState({ riskPortAlerts: types })}
					/>
					{this.state.editMode && this.editActions('medium')}
				</WorkflowDetailWrapper>
				<AlertModal {...this.state.endModal} willTimeout={false} onClose={this.endAndRedirect} />
				<ConfirmModal
					visible={this.state.decisionModalVisible.approve}
					title={`Approve POR #${this.state.portOpeningRequestOnPrem?.id}`}
					question="Are you sure you want to approve this request?"
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={this.handleApprove}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.cancel}
					title={`Cancel POR #${this.state.portOpeningRequestOnPrem?.id}`}
					question="Are you sure you want to cancel this request? This action cannot be undone."
					confirmationField={this.decisionModalConfirmationField()}
					confirmButton={{
						label: 'Cancel Request',
						props: {
							variant: 'primary',
							color: 'negative',
						},
					}}
					onConfirm={() => this.handleCancellation()}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.reject}
					title={`Reject POR #${this.state.portOpeningRequestOnPrem?.id}`}
					question="Are you sure you want to reject this request?"
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={() => this.handleReject()}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.escalate}
					title={`Escalate POR #${this.state.portOpeningRequestOnPrem?.id}`}
					question="Are you sure you want to escalate this request?"
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={() => this.handleEscalate()}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.review}
					title={`Send POR #${this.state.portOpeningRequestOnPrem?.id} to Review`}
					question="Are you sure you want to send this request to review?"
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={this.handleReview}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.draft}
					title={`Send POR #${this.state.portOpeningRequestOnPrem?.id} to Draft`}
					question="Are you sure you want to send this request to draft?"
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={this.handleDraft}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.complete}
					title={`Complete POR #${this.state.portOpeningRequestOnPrem?.id}`}
					question="Are you sure you want to mark this request as complete?"
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={this.handleComplete}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.resubmit}
					title={`Resubmit POR #${this.state.portOpeningRequestOnPrem?.id}`}
					question="Are you sure you want to resubmit this request?"
					confirmationField={this.decisionModalConfirmationField()}
					onConfirm={this.handleUpdate}
					onCancel={this.closeDecisionModal}
				/>
				<ConfirmModal
					visible={this.state.decisionModalVisible.createCHG}
					title={`Create CHG for POR #${this.state.portOpeningRequestOnPrem?.id}`}
					question="Are you sure you want to create a Change Ticket for this request?"
					onConfirm={this.handleCreateCHG}
					onCancel={this.closeDecisionModal}
				/>
			</>
		);
	}
}

const OnPremPortOpeningRequestsDetail = () => {
	return <PortOpeningRequestsDetailWithParams params={useParams()} navigate={useNavigate()} />;
};

export default OnPremPortOpeningRequestsDetail;
